/**
 * Retinal and neural conditions: parameters of the retinal pass and the acuity floors
 * (OPTICS §7; RP D-012, amblyopia D-020, hypertensive retinopathy D-022). Geometry in degrees around fixation.
 */

import type { Stage3, Stage4, Stage5 } from './profile';

export interface RetinaSpot {
  /** Offset from fixation, degrees (+x right, +y up). */
  dx: number;
  dy: number;
  /** Radius, degrees. */
  r: number;
  /** Damage strength 0..1. */
  strength: number;
}

export interface Floater {
  dx: number;
  dy: number;
  rx: number;
  ry: number;
}

export interface RetinaStages {
  glaucoma: Stage4;
  amd: Stage3;
  dr: Stage3;
  hr: Stage4;
  rp: Stage4;
  amblyopia: Stage5;
}

export interface RetinaParams extends RetinaStages {
  /** Dark-adaptation visibility 0..1; 1 = no effect (D-023). */
  dark: number;
  /** Fixation in render pixels, y-up. */
  fixation: [number, number];
}

export function retinaActive(p: RetinaStages & { dark?: number }): boolean {
  return p.glaucoma > 0 || p.amd > 0 || p.dr > 0 || p.hr > 0 || p.rp > 0 || p.amblyopia > 0 || (p.dark ?? 1) < 1;
}

/** Conditions whose defects are fixed on the retina, so the gaze toggle and the crosshair make sense. */
export function gazeRelevant(p: { glaucoma: number; amd: number; dr: number; hr: number; rp: number }): boolean {
  return p.glaucoma > 0 || p.amd > 0 || p.dr > 0 || p.rp > 0 || p.hr >= 3;
}

/** Acuity floors, arcmin, per stage. */
const MAR_RET = {
  glaucoma: [0, 0, 0, 0.4, 1.6],
  amd: [0, 0.6, 1.8, 11],
  dr: [0, 0.4, 1.8, 5],
  hr: [0, 0, 0, 0.8, 4],
  rp: [0, 0, 0.3, 0.8, 3],
} as const;

/**
 * Amblyopia, 5-degree CIS classification by best-corrected visus (D-020):
 * very mild 0.8–0.9, mild 0.4–0.7, moderate 0.2–0.3, severe 0.05–0.1, very severe ≤ 0.04.
 * The floor puts the corrected estimate mid-range at MAR0 = 0.8′.
 */
export const AMBLYOPIA_MAR = [0, 0.86, 1.63, 3.92, 13.3, 33.3] as const;
export const AMBLYOPIA_VISUS: ReadonlyArray<readonly [number, number]> = [
  [1, 1], [0.8, 0.9], [0.4, 0.7], [0.2, 0.3], [0.05, 0.1], [0.01, 0.04],
];
/** Extra contrast loss toward the local mean, per degree. */
export const AMBLYOPIA_CONTRAST = [0, 0.03, 0.06, 0.1, 0.15, 0.2] as const;

/** Gaussian blur that renders a degree of amblyopia, arcmin (σ ≈ 1.4·MAR, as the optical blur ↔ VA mapping of OPTICS §9). */
export function amblyopiaBlurArcmin(stage: Stage5): number {
  return 1.4 * (AMBLYOPIA_MAR[stage] ?? 0);
}

export function retinalMar(p: RetinaStages): number {
  return Math.max(
    MAR_RET.glaucoma[p.glaucoma] ?? 0,
    MAR_RET.amd[p.amd] ?? 0,
    MAR_RET.dr[p.dr] ?? 0,
    MAR_RET.hr[p.hr] ?? 0,
    MAR_RET.rp[p.rp] ?? 0,
    AMBLYOPIA_MAR[p.amblyopia] ?? 0,
  );
}

/** Fixed pseudo-random microhaemorrhage spots; the first five are the stage-1 set. */
const DR_SPOTS: RetinaSpot[] = [
  { dx: -4.5, dy: 2.5, r: 0.9, strength: 0.5 },
  { dx: 3.8, dy: -3.2, r: 0.7, strength: 0.5 },
  { dx: 7.5, dy: 1.5, r: 1.1, strength: 0.45 },
  { dx: -2.0, dy: -5.5, r: 0.8, strength: 0.5 },
  { dx: 1.5, dy: 4.8, r: 0.6, strength: 0.45 },
  { dx: -8.5, dy: -1.5, r: 1.0, strength: 0.55 },
  { dx: 6.0, dy: 5.5, r: 0.8, strength: 0.55 },
  { dx: -5.5, dy: 6.5, r: 0.7, strength: 0.5 },
  { dx: 9.5, dy: -4.5, r: 1.2, strength: 0.55 },
  { dx: 0.5, dy: -8.5, r: 0.9, strength: 0.5 },
];

/** Cotton-wool spots and flame haemorrhages around the posterior pole, hypertensive retinopathy III+ (D-022). */
const HR_SPOTS: RetinaSpot[] = [
  { dx: 7.0, dy: 2.0, r: 1.0, strength: 0.45 },
  { dx: 9.0, dy: -1.5, r: 0.8, strength: 0.4 },
  { dx: 4.0, dy: 4.0, r: 0.7, strength: 0.4 },
  { dx: -3.0, dy: 3.5, r: 0.6, strength: 0.35 },
  { dx: 5.5, dy: -4.0, r: 0.9, strength: 0.4 },
  { dx: -6.0, dy: -2.0, r: 0.7, strength: 0.35 },
  { dx: 2.0, dy: -6.0, r: 0.5, strength: 0.35 },
  { dx: 11.0, dy: 3.0, r: 0.6, strength: 0.4 },
];

export const MAX_SPOTS = 20;

const FLOATERS: Floater[] = [
  { dx: -4.0, dy: 3.0, rx: 2.4, ry: 0.7 },
  { dx: 5.0, dy: -2.0, rx: 1.6, ry: 0.6 },
  { dx: 1.5, dy: 6.0, rx: 1.0, ry: 0.5 },
];

export function drSpots(stage: Stage3): RetinaSpot[] {
  if (stage === 0) return [];
  const spots = stage === 1 ? DR_SPOTS.slice(0, 5) : DR_SPOTS;
  const faint = stage === 1 ? 0.8 : 1;
  return spots.map((s) => ({ ...s, strength: s.strength * faint }));
}

/** All focal spots of the retinal pass: diabetic and hypertensive. */
export function retinaSpots(p: { dr: Stage3; hr: Stage4 }): RetinaSpot[] {
  return [...drSpots(p.dr), ...(p.hr >= 3 ? HR_SPOTS : [])].slice(0, MAX_SPOTS);
}

export function drFloaters(stage: Stage3): Floater[] {
  return stage === 3 ? FLOATERS : [];
}
